/**
 * One way to tell which KPA a stored key means (HR UAT list #24, Oct 2026).
 *
 * A check-in's KPA rows are keyed however the screen that saved them named the KPA:
 * its table name ('culture-people'), its display name ('Culture and People') or a slug
 * of that ('culture-and-people'). kpaKey spells any of these one way, so a KPA is
 * recognised by its table name or its display name, whichever a row or snapshot used.
 */
export const kpaKey = (value?: string | number | null) =>
  String(value ?? '').trim().toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

/** Whether a stored key names the KPA with this table name and display name. */
export function namesKpa(kpa: { key: string; display: string }, stored?: string | number | null): boolean {
  const key = kpaKey(stored)
  return key !== '' && (key === kpaKey(kpa.key) || key === kpaKey(kpa.display))
}

/**
 * The four KPAs: the table name (the key a rating goes under when the check-in has no row
 * for the KPA yet), the short name the screens show, and the display name.
 */
export const EDUVOS_KPAS: readonly { key: string; label: string; display: string }[] = [
  { key: 'operating-financial', label: 'Operating & Financial', display: 'Operating and Financial Results' },
  { key: 'internal-business-processes', label: 'Internal Business Processes', display: 'Internal Business Processes and Enablement' },
  { key: 'culture-people', label: 'Culture & People', display: 'Culture and People' },
  { key: 'customer-stakeholder', label: 'Customer & Stakeholder', display: 'Customer and Stakeholder Relationships' },
]

/** The KPA a stored key names, whichever of its names (or a slug of one) the row was saved under. */
export function eduvosKpa(stored?: string | number | null) {
  const key = kpaKey(stored)
  return EDUVOS_KPAS.find((kpa) => namesKpa(kpa, stored) || (key !== '' && key === kpaKey(kpa.label)))
}

/**
 * A stored KPA key as the screens show it: the KPA's short name, so a row saved under its
 * table name ('culture-people') reads as 'Culture & People' (HR, calibration UAT Oct 2026).
 * Any other KPA reads as it was stored. Display only: the stored key stays the KPA's identity.
 */
export const kpaLabel = (stored?: string | null) => eduvosKpa(stored)?.label ?? String(stored ?? '')

/** The KPA's full display name, for a tooltip; any other KPA as it was stored. */
export const kpaDisplayName = (stored?: string | null) => eduvosKpa(stored)?.display ?? String(stored ?? '')
